import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Header } from "@/components/Header";
import { ProgressIndicator } from "@/components/ProgressIndicator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Download, FileText, FileCheck, Home, ArrowLeft } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useTest } from "@/context/TestContext";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { readSavedTestId, writeSavedTestId } from "@/utils/localStorage";
import { downloadTest, type DownloadFormat, type TestSection } from "@/utils/downloadTest";
import type { InsertTest } from "@shared/schema";

export default function ShareTest() {
  const [, setLocation] = useLocation();
  const { config, blueprint, testName, setTestName, questions, directions, resetTest } = useTest();
  const { toast } = useToast();
  const [localTestName, setLocalTestName] = useState(testName);
  const [savedTestId, setSavedTestId] = useState<string | null>(() => readSavedTestId());

  // Download options state
  const [selectedItems, setSelectedItems] = useState({
    blueprint: false,
    studentCopy: false,
    answerKey: false,
    teacherCopy: false,
  });
  const [fileType, setFileType] = useState("pdf");
  const [downloadAsSingleFile, setDownloadAsSingleFile] = useState(false);

  const mockShareUrl = savedTestId
    ? `https://testcraft.ai/test/${savedTestId}`
    : `https://testcraft.ai/test/${Math.random().toString(36).substring(7)}`;

  const saveTestMutation = useMutation({
    mutationFn: async (testData: InsertTest) => {
      return await apiRequest("POST", "/api/tests", testData);
    },
    onSuccess: (data: any) => {
      setSavedTestId(data.id);
      writeSavedTestId(data.id); // survive a refresh so we don't re-save a duplicate
      queryClient.invalidateQueries({ queryKey: ["/api/tests"] });
      toast({
        title: "Test Saved",
        description: "Your test has been saved successfully!",
      });
    },
    onError: () => {
      toast({
        title: "Save Failed",
        description: "Failed to save test. You can still download it.",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (config && blueprint && questions.length > 0 && !savedTestId) {
      const testData: InsertTest = {
        name: localTestName,
        config,
        blueprint,
        questions,
        directions: directions || "",
      };
      saveTestMutation.mutate(testData);
    }
  }, []);

  const handleItemSelection = (item: keyof typeof selectedItems, checked: boolean) => {
    setSelectedItems(prev => ({
      ...prev,
      [item]: checked
    }));
  };

  const handleDownload = async () => {
    const orderedSections: TestSection[] = [];
    if (selectedItems.blueprint) orderedSections.push("blueprint");
    if (selectedItems.studentCopy) orderedSections.push("studentCopy");
    if (selectedItems.answerKey) orderedSections.push("answerKey");
    if (selectedItems.teacherCopy) orderedSections.push("teacherCopy");

    if (orderedSections.length === 0) {
      toast({
        title: "No Items Selected",
        description: "Please select at least one item to download.",
        variant: "destructive",
      });
      return;
    }

    try {
      await downloadTest(
        {
          name: localTestName,
          blueprint,
          questions,
          directions: directions || "",
          subject: config?.subject,
          readingLevel: config?.readingLevel,
        },
        {
          format: fileType as DownloadFormat,
          sections: orderedSections,
          singleFile: downloadAsSingleFile,
        },
      );
      const labelByFormat: Record<DownloadFormat, string> = {
        pdf: "PDF",
        docx: "DOCX",
        txt: "TXT",
        md: "Markdown",
      };
      const label = labelByFormat[fileType as DownloadFormat];
      toast({
        title: "Download Complete",
        description: downloadAsSingleFile
          ? `Combined ${label} file downloaded`
          : `${orderedSections.length} ${label} file${orderedSections.length === 1 ? "" : "s"} downloaded`,
      });
    } catch (err) {
      console.error("Failed to download test:", err);
      toast({
        title: "Download failed",
        description: "Could not build the document. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(mockShareUrl);
    toast({
      title: "Link Copied",
      description: "Test link copied to clipboard!",
    });
  };


  // Step back to Finalize to tweak directions or question order. Unlike the
  // exit actions below, this keeps the test state intact.
  const handleBackToFinalize = () => {
    setLocation("/create/finalize");
  };

  const handleCreateAnother = () => {
    resetTest();
    setLocation("/create/identify");
  };

  const handleBackToDashboard = () => {
    resetTest();
    setLocation("/dashboard");
  };

  return (
    <div className="min-h-screen bg-background">
      <Header pageTitle="Test Complete" />

      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <ProgressIndicator currentStep={7} />

        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-success rounded-full flex items-center justify-center animate-in zoom-in duration-500">
              <CheckCircle2 className="w-16 h-16 text-success-foreground" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-3">Your test is ready!</h1>
          <p className="text-lg text-muted-foreground">
            Share your test with students or download for later use
          </p>
        </div>

        <Card className="mb-8">
          <CardContent className="pt-6 space-y-6">
            <div className="grid grid-cols-4 items-end gap-6 pb-6 border-b">
              {/* Name fills the left half (out to the middle); Total Questions
                  spans the right half with centered content so it sits ~3/4
                  across the pane. */}
              <div className="col-span-2 space-y-2">
                <Label htmlFor="test-name" className="text-base font-semibold">
                  Test Name
                </Label>
                <Input
                  id="test-name"
                  value={localTestName}
                  onChange={(e) => {
                    setLocalTestName(e.target.value);
                    setTestName(e.target.value);
                  }}
                  className="text-lg"
                  data-testid="input-test-name"
                />
              </div>
              <div className="col-span-2 text-center">
                <p className="text-sm text-muted-foreground mb-1">Total Questions</p>
                <p className="text-3xl font-bold text-primary" data-testid="text-total-questions">{questions.length}</p>
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-base font-semibold">Download Options</Label>
              
              {/* Item Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Select Items to Download</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div 
                    className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => handleItemSelection('blueprint', !selectedItems.blueprint)}
                  >
                    <Checkbox
                      id="blueprint"
                      checked={selectedItems.blueprint}
                      onCheckedChange={(checked) => handleItemSelection('blueprint', checked as boolean)}
                      data-testid="checkbox-blueprint"
                    />
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <Label htmlFor="blueprint" className="text-sm font-medium cursor-pointer">
                          Blueprint
                        </Label>
                        <p className="text-xs text-muted-foreground">Topic breakdown & stats</p>
                      </div>
                    </div>
                  </div>

                  <div 
                    className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => handleItemSelection('studentCopy', !selectedItems.studentCopy)}
                  >
                    <Checkbox
                      id="studentCopy"
                      checked={selectedItems.studentCopy}
                      onCheckedChange={(checked) => handleItemSelection('studentCopy', checked as boolean)}
                      data-testid="checkbox-student"
                    />
                    <div className="flex items-center gap-2">
                      <Download className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <Label htmlFor="studentCopy" className="text-sm font-medium cursor-pointer">
                          Student Copy
                        </Label>
                        <p className="text-xs text-muted-foreground">Questions only</p>
                      </div>
                    </div>
                  </div>

                  <div 
                    className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => handleItemSelection('answerKey', !selectedItems.answerKey)}
                  >
                    <Checkbox
                      id="answerKey"
                      checked={selectedItems.answerKey}
                      onCheckedChange={(checked) => handleItemSelection('answerKey', checked as boolean)}
                      data-testid="checkbox-answer-key"
                    />
                    <div className="flex items-center gap-2">
                      <FileCheck className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <Label htmlFor="answerKey" className="text-sm font-medium cursor-pointer">
                          Answer Key
                        </Label>
                        <p className="text-xs text-muted-foreground">With correct answers</p>
                      </div>
                    </div>
                  </div>

                  <div 
                    className="flex items-center space-x-3 p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => handleItemSelection('teacherCopy', !selectedItems.teacherCopy)}
                  >
                    <Checkbox
                      id="teacherCopy"
                      checked={selectedItems.teacherCopy}
                      onCheckedChange={(checked) => handleItemSelection('teacherCopy', checked as boolean)}
                      data-testid="checkbox-teacher-copy"
                    />
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <Label htmlFor="teacherCopy" className="text-sm font-medium cursor-pointer">
                          Teacher Copy
                        </Label>
                        <p className="text-xs text-muted-foreground">Questions with notes</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* File Type Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">File Type</Label>
                <RadioGroup value={fileType} onValueChange={setFileType} className="flex gap-6">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="pdf" id="pdf" />
                    <Label htmlFor="pdf" className="text-sm cursor-pointer">PDF</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="docx" id="docx" />
                    <Label htmlFor="docx" className="text-sm cursor-pointer">DOCX</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="txt" id="txt" />
                    <Label htmlFor="txt" className="text-sm cursor-pointer">TXT</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="md" id="md" />
                    <Label htmlFor="md" className="text-sm cursor-pointer">Markdown</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Single File Option */}
              <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50">
                <Checkbox
                  id="singleFile"
                  checked={downloadAsSingleFile}
                  onCheckedChange={(checked) => setDownloadAsSingleFile(checked as boolean)}
                  data-testid="checkbox-single-file"
                />
                <div>
                  <Label htmlFor="singleFile" className="text-sm font-medium cursor-pointer">
                    Download as single file
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Combine all selected items into one file
                  </p>
                </div>
              </div>

              {/* Download Button */}
              <Button 
                onClick={handleDownload} 
                className="w-full" 
                size="lg"
                data-testid="button-download"
              >
                Download Selected Items <Download className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="outline"
            size="lg"
            onClick={handleBackToFinalize}
            data-testid="button-back-finalize"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Test
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={handleBackToDashboard}
            data-testid="button-dashboard"
          >
            Back to Dashboard <Home className="w-5 h-5" />
          </Button>
          <Button size="lg" onClick={handleCreateAnother} data-testid="button-create-another">
            Create Another Test
          </Button>
        </div>
      </main>
    </div>
  );
}
